import { FUNNEL_VERSIONS, FUNNELS } from './constants';
import { EVENTS, track } from './track';

type TrackProperties = { [key: string]: any };

const withFunnelMetadata = (
  funnelName: string,
  funnelVersion: string,
  properties: TrackProperties = {}
): TrackProperties => ({
  funnel_name: funnelName,
  funnel_version: funnelVersion,
  ...properties,
});

export function trackSignupFunnel(
  eventName: string,
  properties?: TrackProperties
) {
  const searchParams = new URLSearchParams(window.location.search);
  const isBypassFromUrl = searchParams.get('bypassPlanSelection') === 'true';
  const isBypass =
    properties?.bypassPlanSelection ??
    properties?.bypass_plan_selection ??
    isBypassFromUrl;

  track(
    eventName,
    withFunnelMetadata(FUNNELS.signupPurchase, FUNNEL_VERSIONS.signupPurchase, {
      bypassPlanSelection: Boolean(isBypass),
      bypass_plan_selection: Boolean(isBypass),
      ...properties,
    })
  );
}

export function trackQuizFunnel(
  eventName: string,
  properties?: TrackProperties
) {
  track(
    eventName,
    withFunnelMetadata(
      FUNNELS.quizResults,
      FUNNEL_VERSIONS.quizResults,
      properties
    )
  );
}

export function trackReactivationFunnel(
  eventName: string,
  properties?: TrackProperties
) {
  track(
    eventName,
    withFunnelMetadata(
      FUNNELS.reactivation,
      FUNNEL_VERSIONS.reactivation,
      properties
    )
  );
}

// ==========================================
// Tracking Payload Helper Functions
// ==========================================

export const trackSignupPlanSelectionViewed = (
  signupVariant: string,
  planType: string,
  selectedPlanId?: string,
  country?: string
) => {
  trackSignupFunnel(EVENTS.signupPlanSelectionViewed, {
    signup_variant: signupVariant,
    plan_type: planType,
    selected_plan_id: selectedPlanId,
    country: country,
    page_path: window.location.pathname,
  });
};

export const trackSignupPlanSelected = (
  planType: string,
  selectedPlanId: string,
  interval?: string,
  intervalCount?: number
) => {
  trackSignupFunnel(EVENTS.signupPlanSelected, {
    plan_type: planType,
    selected_plan_id: selectedPlanId,
    selected_plan_interval: interval,
    selected_plan_interval_count: intervalCount,
  });
};

export const trackSignupCheckoutStarted = (
  planType: string,
  selectedPlanId: string,
  nextStep: string
) => {
  trackSignupFunnel(EVENTS.signupCheckoutStarted, {
    plan_type: planType,
    selected_plan_id: selectedPlanId,
    next_step: nextStep,
  });
};

export const trackSignupAccountStepViewed = (selectedPlanId?: string) => {
  trackSignupFunnel(EVENTS.signupAccountStepViewed, {
    selected_plan_id: selectedPlanId,
  });
};

export const trackSignupAccountCreated = (
  selectedPlanId?: string,
  emailDomain?: string
) => {
  trackSignupFunnel(EVENTS.signupAccountCreated, {
    selected_plan_id: selectedPlanId,
    email_domain: emailDomain,
  });
};

export const trackSignupPaymentStepViewed = (
  selectedPlanId?: string,
  totalPrice?: number
) => {
  trackSignupFunnel(EVENTS.signupPaymentStepViewed, {
    selected_plan_id: selectedPlanId,
    total_price: totalPrice,
  });
};

export const trackSignupCheckoutSectionViewed = (
  sectionName: string,
  selectedPlanId?: string
) => {
  trackSignupFunnel(EVENTS.signupCheckoutSectionViewed, {
    section_name: sectionName,
    selected_plan_id: selectedPlanId,
  });
};

export const trackSignupPaymentMethodSelected = (
  paymentMethod: string,
  selectedPlanId?: string
) => {
  trackSignupFunnel(EVENTS.signupPaymentMethodSelected, {
    payment_method: paymentMethod,
    selected_plan_id: selectedPlanId,
  });
};

export const trackSignupPaymentSubmitted = (
  paymentMethod: string,
  selectedPlanId?: string,
  totalPrice?: number
) => {
  trackSignupFunnel(EVENTS.signupPaymentSubmitted, {
    payment_method: paymentMethod,
    selected_plan_id: selectedPlanId,
    total_price: totalPrice,
  });
};

export const trackSignupPaymentFailed = (
  paymentMethod: string,
  failureStage: string,
  errorMessage: string,
  selectedPlanId?: string
) => {
  trackSignupFunnel(EVENTS.signupPaymentFailed, {
    payment_method: paymentMethod,
    failure_stage: failureStage,
    error_message: errorMessage,
    selected_plan_id: selectedPlanId,
  });
};

export const trackSignupPaymentCompleted = (
  paymentMethod: string,
  selectedPlanId?: string,
  totalPrice?: number
) => {
  trackSignupFunnel(EVENTS.signupPaymentCompleted, {
    payment_method: paymentMethod,
    selected_plan_id: selectedPlanId,
    total_price: totalPrice,
  });
};

export const trackSignupPurchaseCompleted = (
  selectedPlanId?: string,
  totalPrice?: number,
  trackedPriceIds?: string
) => {
  trackSignupFunnel(EVENTS.signupPurchaseCompleted, {
    selected_plan_id: selectedPlanId,
    total_price: totalPrice,
    tracked_price_ids: trackedPriceIds,
  });
};

// Subscription Page Upsells (Pre-checkout)
export const trackSubscriptionUpsellViewed = (
  availableAddons: string,
  selectedPlanId?: string
) => {
  trackSignupFunnel(EVENTS.subscriptionUpsellViewed, {
    available_addons: availableAddons,
    selected_plan_id: selectedPlanId,
  });
};

export const trackSubscriptionAddonToggle = (
  isAdded: boolean,
  addonType: string,
  selectedPlanId?: string
) => {
  trackSignupFunnel(
    isAdded
      ? EVENTS.subscriptionAddonSelected
      : EVENTS.subscriptionAddonRemoved,
    {
      addon_type: addonType,
      selected_plan_id: selectedPlanId,
    }
  );
};

export const trackSubscriptionUpsellDeclined = (
  addonType: string,
  selectedPlanId?: string
) => {
  trackSignupFunnel(EVENTS.subscriptionUpsellDeclined, {
    addon_type: addonType,
    selected_plan_id: selectedPlanId,
  });
};

// Checkout Page Upsells (On Checkout Summary)
export const trackCheckoutUpsellViewed = (
  availableAddons: string,
  selectedPlanId?: string
) => {
  trackSignupFunnel(EVENTS.checkoutUpsellViewed, {
    available_addons: availableAddons,
    selected_plan_id: selectedPlanId,
  });
};

export const trackCheckoutAddonToggle = (
  isAdded: boolean,
  addonType: string,
  selectedPlanId?: string
) => {
  trackSignupFunnel(
    isAdded ? EVENTS.checkoutAddonSelected : EVENTS.checkoutAddonRemoved,
    {
      addon_type: addonType,
      selected_plan_id: selectedPlanId,
    }
  );
};

// Post Purchase Upsells (After Base Payment)
export const trackPostPurchaseUpsellViewed = (
  addonType: string,
  selectedPlanId?: string
) => {
  trackSignupFunnel(EVENTS.postPurchaseUpsellViewed, {
    addon_type: addonType,
    selected_plan_id: selectedPlanId,
  });
};

export const trackPostPurchaseAddonSelected = (
  addonType: string,
  selectedPlanId?: string
) => {
  trackSignupFunnel(EVENTS.postPurchaseAddonSelected, {
    addon_type: addonType,
    selected_plan_id: selectedPlanId,
  });
};

export const trackPostPurchaseUpsellDeclined = (
  addonType: string,
  selectedPlanId?: string
) => {
  trackSignupFunnel(EVENTS.postPurchaseUpsellDeclined, {
    addon_type: addonType,
    selected_plan_id: selectedPlanId,
  });
};

// Standalone Addon Journey (Existing User Dashboard)
export const trackAddonViewed = (availableAddons: string) => {
  trackSignupFunnel(EVENTS.addonViewed, {
    available_addons: availableAddons,
  });
};

export const trackAddonSelected = (
  addonType: string,
  paymentMethod: string,
  totalPrice: number
) => {
  trackSignupFunnel(EVENTS.addonSelected, {
    addon_type: addonType,
    payment_method: paymentMethod,
    total_price: totalPrice,
  });
};

export const trackAddonRemoved = (addonType: string) => {
  trackSignupFunnel(EVENTS.addonRemoved, {
    addon_type: addonType,
  });
};

export const trackAddonPaymentStepViewed = (
  addonType: string,
  totalPrice: number
) => {
  trackSignupFunnel(EVENTS.addonPaymentStepViewed, {
    addon_type: addonType,
    total_price: totalPrice,
  });
};

export const trackAddonPaymentMethodSelected = (
  paymentMethod: string,
  addonType: string
) => {
  trackSignupFunnel(EVENTS.addonPaymentMethodSelected, {
    payment_method: paymentMethod,
    addon_type: addonType,
  });
};

export const trackAddonPaymentSubmitted = (
  paymentMethod: string,
  addonType: string,
  totalPrice: number
) => {
  trackSignupFunnel(EVENTS.addonPaymentSubmitted, {
    payment_method: paymentMethod,
    addon_type: addonType,
    total_price: totalPrice,
  });
};

export const trackAddonPaymentFailed = (
  paymentMethod: string,
  addonType: string,
  failureStage: string,
  errorMessage: string,
  totalPrice: number
) => {
  trackSignupFunnel(EVENTS.addonPaymentFailed, {
    payment_method: paymentMethod,
    addon_type: addonType,
    failure_stage: failureStage,
    error_message: errorMessage,
    total_price: totalPrice,
  });
};

export const trackAddonPaymentCompleted = (
  paymentMethod: string,
  addonType: string,
  totalPrice: number
) => {
  trackSignupFunnel(EVENTS.addonPaymentCompleted, {
    payment_method: paymentMethod,
    addon_type: addonType,
    total_price: totalPrice,
  });
};

export const trackAddonPurchaseCompleted = (
  paymentMethod: string,
  addonType: string,
  totalPrice: number,
  trackedPriceIds?: string
) => {
  trackSignupFunnel(EVENTS.addonPurchaseCompleted, {
    payment_method: paymentMethod,
    addon_type: addonType,
    total_price: totalPrice,
    tracked_price_ids: trackedPriceIds,
  });
};

// Promo code tracking
export const trackPromoCodeApplied = (promoCode: string) => {
  trackSignupFunnel(EVENTS.promoCodeApplied, {
    promo_code: promoCode,
  });
};

export const trackPromoCodeSuccess = (
  promoCode: string,
  discountAmount?: number,
  percentOff?: number
) => {
  trackSignupFunnel(EVENTS.promoCodeSuccess, {
    promo_code: promoCode,
    discount_amount: discountAmount,
    percent_off: percentOff,
  });
};

export const trackPromoCodeFailed = (
  promoCode: string,
  errorMessage: string
) => {
  trackSignupFunnel(EVENTS.promoCodeFailed, {
    promo_code: promoCode,
    error_message: errorMessage,
  });
};

// Reactivation Flow tracking functions (reusing existing update_subscription, purchase, and change_plan events)
export const trackReactivationViewed = (
  source: 'page' | 'update_subscription' | 'signup_extras',
  scenario?: string,
  selectedPlanId?: string,
  hasExistingPaymentMethod?: boolean
) => {
  trackReactivationFunnel(EVENTS.updateSubscription, {
    is_reactivation: true,
    action: 'viewed',
    source,
    scenario,
    selected_plan_id: selectedPlanId,
    has_existing_payment_method: hasExistingPaymentMethod,
    page_path: window.location.pathname,
  });
};

export const trackReactivationSubmitted = (
  source: 'page' | 'update_subscription',
  paymentMethod?: string,
  selectedPlanId?: string,
  totalPrice?: number | string,
  scenario?: string
) => {
  trackReactivationFunnel(EVENTS.updateSubscription, {
    is_reactivation: true,
    action: 'submitted',
    source,
    payment_method: paymentMethod,
    selected_plan_id: selectedPlanId,
    total_price: totalPrice,
    scenario,
  });
};

export const trackReactivationFailed = (
  source: 'page' | 'update_subscription',
  paymentMethod?: string,
  errorMessage?: string,
  selectedPlanId?: string,
  failureStage?: string
) => {
  trackReactivationFunnel(EVENTS.updateSubscription, {
    is_reactivation: true,
    action: 'failed',
    source,
    payment_method: paymentMethod,
    error_message: errorMessage,
    selected_plan_id: selectedPlanId,
    failure_stage: failureStage,
  });
};

export const trackReactivationCompleted = (
  source: 'page' | 'update_subscription',
  paymentMethod?: string,
  selectedPlanId?: string,
  totalPrice?: number | string,
  scenario?: string
) => {
  trackReactivationFunnel(EVENTS.purchase, {
    is_reactivation: true,
    action: 'completed',
    source,
    payment_method: paymentMethod,
    selected_plan_id: selectedPlanId,
    total_price: totalPrice,
    scenario,
  });
  trackReactivationFunnel(EVENTS.updateSubscription, {
    is_reactivation: true,
    action: 'completed',
    source,
    payment_method: paymentMethod,
    selected_plan_id: selectedPlanId,
    total_price: totalPrice,
    scenario,
  });
};

export const trackReactivationPlanSelected = (
  selectedPlanId: string,
  currentPlanId?: string,
  totalPrice?: number | string
) => {
  trackReactivationFunnel(EVENTS.changePlan, {
    is_reactivation: true,
    currentPlan: currentPlanId,
    newPlan: selectedPlanId,
    total_price: totalPrice,
  });
};
